/**
 * The pre-approval surface: the pitch, the application form, and the notices for
 * pending / rejected / suspended partners. Rendered by the portal shell instead
 * of the sidebar layout, since none of the portal's sections exist yet.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { classNames } from '~/utils/classNames';
import { apiApplyAffiliate } from '~/lib/api/affiliates';
import { CARD, Field, TERMS } from './shared';

const STEPS = [
  { step: '01', title: 'Apply', body: 'Tell us where your audience lives. Every application is read by a human.' },
  { step: '02', title: 'Share your link', body: 'Approved partners get a personal link that attributes every signup.' },
  { step: '03', title: 'Earn', body: 'Twenty percent of what they pay, for six months — tracked live in your portal.' },
];

/** Newsprint-column strip of the program's headline terms. */
export function TermsStrip() {
  return (
    // Three columns wait for lg once the (locked) portal sidebar appears at md — see CellGrid.
    <div className="grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-hairline bg-hairline sm:grid-cols-3 md:grid-cols-1 lg:grid-cols-3">
      {TERMS.map((t) => (
        <div key={t.value} className="bg-surface px-5 py-4">
          <div className="ui-serif text-[26px] leading-none text-primary">{t.value}</div>
          <div className="ui-caption mt-2 text-muted">{t.caption}</div>
        </div>
      ))}
    </div>
  );
}

export function ApplyView() {
  return (
    <div className="flex flex-col gap-8">
      <TermsStrip />

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        {STEPS.map((s) => (
          <div key={s.step}>
            <div className="ui-mono text-[11px] text-accent">{s.step}</div>
            <h3 className="mt-2 text-[13px] font-medium text-primary">{s.title}</h3>
            <p className="mt-1 text-[12.5px] leading-relaxed text-muted">{s.body}</p>
          </div>
        ))}
      </div>

      <ApplyForm />
    </div>
  );
}

/** Blank → undefined; a bare host gets `https://` so pasted handles just work. */
function normalizeUrl(value: string): string | undefined {
  const trimmed = value.trim();

  if (!trimmed) {
    return undefined;
  }

  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function urlError(value: string, label: string): string | undefined {
  const normalized = normalizeUrl(value);

  if (!normalized) {
    return undefined;
  }

  try {
    const parsed = new URL(normalized);

    if (!parsed.hostname.includes('.')) {
      throw new Error('no tld');
    }

    return undefined;
  } catch {
    return `That doesn’t look like a valid ${label} link.`;
  }
}

const AUDIENCE_MAX = 2000;

function ApplyForm() {
  const qc = useQueryClient();
  const [promoChannels, setPromoChannels] = useState('');
  const [audienceNote, setAudienceNote] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const apply = useMutation({
    mutationFn: () =>
      apiApplyAffiliate({
        promoChannels: promoChannels.trim() || undefined,
        audienceNote: audienceNote.trim() || undefined,
        websiteUrl: normalizeUrl(websiteUrl),
        youtubeUrl: normalizeUrl(youtubeUrl),
        instagramUrl: normalizeUrl(instagramUrl),
      }),
    onSuccess: (result) => {
      toast.success('Application submitted — we’ll be in touch by email.');

      // Flip to the "under review" state from the mutation's own response rather
      // than waiting on a refetch — otherwise the filled-in form sits there for a
      // whole round trip after the success toast, which reads as a failed submit.
      qc.setQueryData(['affiliate', 'me'], (prev: unknown) => ({
        ...(typeof prev === 'object' && prev ? prev : {}),
        affiliate: result.affiliate,
      }));
      qc.invalidateQueries({ queryKey: ['affiliate', 'me'] });
    },
    onError: (err: any) => {
      const message = err?.response?.data?.message;
      toast.error(typeof message === 'string' ? message : 'Could not submit your application.');
    },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();

    const next: Record<string, string> = {};

    if (!promoChannels.trim()) {
      next.promoChannels = 'Tell us where you’d promote Dunea.';
    }

    const website = urlError(websiteUrl, 'website');
    const youtube = urlError(youtubeUrl, 'YouTube');
    const instagram = urlError(instagramUrl, 'Instagram');

    if (website) {
      next.websiteUrl = website;
    }

    if (youtube) {
      next.youtubeUrl = youtube;
    }

    if (instagram) {
      next.instagramUrl = instagram;
    }

    setErrors(next);

    if (Object.keys(next).length === 0) {
      apply.mutate();
    }
  }

  return (
    <form className={classNames(CARD, 'overflow-hidden')} onSubmit={submit} noValidate>
      <div className="border-b border-hairline px-6 py-5">
        <h2 className="text-[15px] font-medium text-primary">Partner application</h2>
        <p className="mt-1 text-[12.5px] text-muted">
          Two short sections. Nothing here is published — it’s only read by our team.
        </p>
      </div>

      <div className="px-6 py-6">
        <SectionHead step="01" title="How you’ll promote Dunea" hint="The more specific, the faster the review." />

        <div className="flex flex-col gap-4">
          <Field label="Primary channel" required error={errors.promoChannels}>
            <input
              className={classNames('ui-input', errors.promoChannels && 'border-danger/50')}
              placeholder="e.g. YouTube tutorials, a weekly design newsletter, agency clients"
              value={promoChannels}
              onChange={(e) => {
                setPromoChannels(e.target.value);
                setErrors((prev) => ({ ...prev, promoChannels: '' }));
              }}
            />
          </Field>

          <Field
            label="About your audience"
            optional
            counter={audienceNote.length > 0 ? `${audienceNote.length}/${AUDIENCE_MAX}` : undefined}
          >
            <textarea
              className="ui-input min-h-[104px] resize-y leading-relaxed"
              maxLength={AUDIENCE_MAX}
              placeholder="Who follows you, roughly how many, and why Dunea fits what they’re building."
              value={audienceNote}
              onChange={(e) => setAudienceNote(e.target.value)}
            />
          </Field>
        </div>

        <div className="ui-hr my-7" />

        <SectionHead step="02" title="Your channels" hint="All optional — add whichever show your reach best." />

        <div className="flex flex-col gap-4">
          <LinkField
            icon="i-ph:globe-simple"
            label="Website"
            placeholder="yoursite.com"
            value={websiteUrl}
            error={errors.websiteUrl}
            onChange={(v) => {
              setWebsiteUrl(v);
              setErrors((prev) => ({ ...prev, websiteUrl: '' }));
            }}
          />
          <LinkField
            icon="i-ph:youtube-logo"
            label="YouTube"
            placeholder="youtube.com/@yourchannel"
            value={youtubeUrl}
            error={errors.youtubeUrl}
            onChange={(v) => {
              setYoutubeUrl(v);
              setErrors((prev) => ({ ...prev, youtubeUrl: '' }));
            }}
          />
          <LinkField
            icon="i-ph:instagram-logo"
            label="Instagram"
            placeholder="instagram.com/yourhandle"
            value={instagramUrl}
            error={errors.instagramUrl}
            onChange={(v) => {
              setInstagramUrl(v);
              setErrors((prev) => ({ ...prev, instagramUrl: '' }));
            }}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-hairline bg-raised/40 px-6 py-4">
        <p className="text-[12px] text-muted">Reviewed by hand. You’ll get an email either way.</p>
        <button type="submit" className="ui-btn-accent" disabled={apply.isPending}>
          {apply.isPending ? 'Submitting…' : 'Submit application'}
        </button>
      </div>
    </form>
  );
}

function SectionHead({ step, title, hint }: { step: string; title: string; hint?: string }) {
  return (
    <div className="mb-5 flex items-baseline gap-3">
      <span className="ui-mono text-[11px] text-faint">{step}</span>
      <div>
        <h3 className="text-[13px] font-medium text-primary">{title}</h3>
        {hint && <p className="mt-0.5 text-[12px] text-muted">{hint}</p>}
      </div>
    </div>
  );
}

/** An icon-prefixed URL input — the icon sits inside the field, not beside it. */
function LinkField({
  icon,
  label,
  placeholder,
  value,
  onChange,
  error,
}: {
  icon: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
}) {
  return (
    <Field label={label} optional error={error}>
      <div
        className={classNames(
          'flex items-center gap-2.5 rounded-lg border bg-sunken px-3 transition-colors focus-within:border-prominent',
          error ? 'border-danger/50' : 'border-hairline',
        )}
      >
        <span className={classNames(icon, 'shrink-0 text-[15px] text-muted')} />
        <input
          type="text"
          inputMode="url"
          autoComplete="off"
          spellCheck={false}
          className="w-full bg-transparent py-2.5 font-geist text-[13px] text-primary outline-none placeholder:text-muted"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </Field>
  );
}

// ── Status notices ───────────────────────────────────────────────────────────

function Notice({
  icon,
  tone,
  ring,
  title,
  body,
}: {
  icon: string;
  /** Static utility classes only — Uno extracts literals, never computed strings. */
  tone: string;
  ring: string;
  title: string;
  body: string;
}) {
  return (
    <div className="flex flex-col gap-8">
      <div className={classNames(CARD, 'flex gap-4 p-6')}>
        <span className={classNames('grid h-9 w-9 shrink-0 place-items-center rounded-full border bg-raised', ring)}>
          <span className={classNames(icon, 'text-[16px]', tone)} />
        </span>
        <div>
          <h2 className="text-[15px] font-medium text-primary">{title}</h2>
          <p className="mt-1.5 max-w-xl text-[13px] leading-relaxed text-secondary">{body}</p>
        </div>
      </div>
      <TermsStrip />
    </div>
  );
}

export const PendingNotice = () => (
  <Notice
    icon="i-ph:clock"
    tone="text-warning"
    ring="border-warning/25"
    title="Your application is under review"
    body="Every partner is reviewed by hand, so this takes a few days rather than a few minutes. We’ll email you the moment there’s a decision — nothing else to do for now."
  />
);

export const RejectedNotice = () => (
  <Notice
    icon="i-ph:prohibit"
    tone="text-danger"
    ring="border-danger/25"
    title="Not approved this time"
    body="Your application wasn’t approved. Check your email for the details — if your reach changes, you’re welcome to get in touch about reapplying."
  />
);

export const SuspendedNotice = () => (
  <Notice
    icon="i-ph:warning-circle"
    tone="text-muted"
    ring="border-subtle"
    title="Your partner account is suspended"
    body="Referrals and commissions are paused while your account is suspended. Contact us if you think this is a mistake."
  />
);
